import React, { useState, useEffect, memo } from 'react';
import { Box, Typography, CircularProgress, Alert } from '@mui/material';
import api from '../../config/axios';

interface PDFViewerProps {
  invoiceId: number;
  invoiceNumber: string;
  height?: string;
}

const PDFViewer: React.FC<PDFViewerProps> = memo(({ 
  invoiceId, 
  invoiceNumber, 
  height = '70vh' 
}) => {
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadPDF = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await api.get(`/invoices/${invoiceId}/pdf`, {
          responseType: 'blob'
        });

        // Create object URL from blob
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        setPdfUrl(url);

      } catch (error: any) {
        console.error('PDF load error:', error);
        if (error.response?.status === 404) {
          setError('PDF soubor nebyl nalezen pro tuto fakturu.');
        } else {
          setError('Chyba při načítání PDF souboru.');
        }
      } finally {
        setLoading(false);
      }
    };

    loadPDF();

    // Cleanup function to revoke object URL
    return () => {
      if (pdfUrl) {
        window.URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [invoiceId]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (pdfUrl) {
        window.URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [pdfUrl]);

  if (loading) {
    return (
      <Box 
        sx={{ 
          height, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          border: '1px solid #e0e0e0',
          borderRadius: 1
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={40} />
          <Typography variant="body2" sx={{ mt: 2 }}>
            Načítám PDF...
          </Typography>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box 
        sx={{ 
          height, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          border: '1px solid #e0e0e0',
          borderRadius: 1
        }}
      >
        <Alert severity="error" sx={{ maxWidth: 400 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  if (!pdfUrl) {
    return (
      <Box 
        sx={{ 
          height, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          border: '1px solid #e0e0e0',
          borderRadius: 1
        }}
      >
        <Typography variant="body2" color="textSecondary">
          PDF není k dispozici
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height,
        border: '1px solid #e0e0e0',
        borderRadius: 1,
        overflow: 'hidden'
      }}
    >
      <iframe
        src={pdfUrl}
        style={{
          width: '100%',
          height: '100%',
          border: 'none'
        }}
        title={`PDF náhled faktury ${invoiceNumber}`}
      />
    </Box>
  );
});

export default PDFViewer;

